/**
 * usePushNotifications Hook
 *
 * Manages push notification permissions, FCM token registration,
 * and foreground message handling.
 *
 * Usage:
 * ```tsx
 * const {
 *   isSupported,
 *   permission,
 *   isEnabled,
 *   isLoading,
 *   requestPermission,
 *   enableNotifications,
 *   disableNotifications,
 * } = usePushNotifications();
 * ```
 */

"use client";

import { sendPushNotificationDebugEmail } from "@/actions/debug-email";
import {
  saveFCMToken,
  updatePushNotificationSettings,
} from "@/actions/push-notification";
import { useAuth } from "@/contexts/AuthContext";
import {
  deleteFCMToken,
  getFCMToken,
  getNotificationPermission,
  getPushSupportStatus,
  isPushSupported,
  onForegroundMessage,
  type PushSupportStatus,
  requestNotificationPermission,
} from "@/lib/fcm";
import { addToast } from "@heroui/toast";
import { useTranslations } from "next-intl";
import { useCallback, useEffect, useState } from "react";

// VAPID key from Firebase Console > Project Settings > Cloud Messaging > Web Push certificates
const VAPID_KEY = process.env.NEXT_PUBLIC_FIREBASE_VAPID_KEY || "";

export interface UsePushNotificationsReturn {
  /** Whether push notifications are supported in this browser */
  isSupported: boolean;
  /** Detailed support status for platform-specific messaging */
  supportStatus: PushSupportStatus;
  /** Current notification permission status */
  permission: NotificationPermission | "unsupported";
  /** Whether notifications are enabled for the current user */
  isEnabled: boolean;
  /** Loading state for async operations */
  isLoading: boolean;
  /** Current FCM token (null if not registered) */
  token: string | null;
  /** Request notification permission from the user */
  requestPermission: () => Promise<boolean>;
  /** Enable push notifications (request permission + register token) */
  enableNotifications: () => Promise<boolean>;
  /** Disable push notifications (remove token) */
  disableNotifications: () => Promise<boolean>;
}

export function usePushNotifications(): UsePushNotificationsReturn {
  const { user, refreshUser } = useAuth();
  const t = useTranslations();

  const [isSupported, setIsSupported] = useState(false);
  const [supportStatus, setSupportStatus] =
    useState<PushSupportStatus>("unsupported");
  const [permission, setPermission] = useState<
    NotificationPermission | "unsupported"
  >("unsupported");
  const [isEnabled, setIsEnabled] = useState(false);
  const [isLoading, setIsLoading] = useState(false);
  const [token, setToken] = useState<string | null>(null);

  // Check if push is supported and get initial permission status
  useEffect(() => {
    const status = getPushSupportStatus();
    setSupportStatus(status);
    setIsSupported(status === "supported");

    if (status === "supported") {
      setPermission(getNotificationPermission());
    }
  }, []);

  // Check if user has notifications enabled
  useEffect(() => {
    if (user?.pushNotificationsEnabled !== undefined) {
      setIsEnabled(user.pushNotificationsEnabled);
    }
  }, [user?.pushNotificationsEnabled]);

  // Set up foreground message handler
  useEffect(() => {
    if (!isSupported || permission !== "granted") {
      return;
    }

    const unsubscribe = onForegroundMessage((payload) => {
      // Show toast for foreground messages
      if (payload.notification) {
        addToast({
          title:
            payload.notification.title || t("notifications.newNotification"),
          description: payload.notification.body,
          color: "primary",
          severity: "primary",
        });
      }
    });

    return () => {
      unsubscribe?.();
    };
  }, [isSupported, permission, t]);

  /**
   * Request notification permission from the user
   */
  const requestPermission = useCallback(async (): Promise<boolean> => {
    if (!isSupported) {
      return false;
    }

    setIsLoading(true);

    try {
      const result = await requestNotificationPermission();
      setPermission(result);

      return result === "granted";
    } catch (error) {
      console.error("Error requesting permission:", error);
      return false;
    } finally {
      setIsLoading(false);
    }
  }, [isSupported]);

  /**
   * Enable push notifications
   * Requests permission, gets FCM token, and saves it to the server
   */
  const enableNotifications = useCallback(async (): Promise<boolean> => {
    // Collect debug info for email reporting
    const debugInfo: Record<string, unknown> = {
      timestamp: new Date().toISOString(),
      userAgent: typeof navigator !== "undefined" ? navigator.userAgent : "N/A",
      isSupported,
      supportStatus,
      hasUser: !!user,
      userId: user?.id,
      hasVapidKey: !!VAPID_KEY,
      initialPermission: typeof Notification !== "undefined" ? Notification.permission : "N/A",
    };

    const sendDebugEmail = async (failedAt: string, error?: unknown) => {
      debugInfo.failedAt = failedAt;
      debugInfo.error = error instanceof Error ? error.message : String(error);
      await sendPushNotificationDebugEmail(debugInfo).catch(console.error);
    };

    if (!isSupported) {
      console.error("Push notifications not supported");
      await sendDebugEmail("isSupported check");
      return false;
    }

    if (!user) {
      console.error("User not authenticated");
      await sendDebugEmail("user check");
      addToast({
        title: t("common.error"),
        description: t("auth.loginRequired"),
        color: "danger",
        severity: "danger",
      });
      return false;
    }

    if (!VAPID_KEY) {
      console.error("VAPID key not configured");
      await sendDebugEmail("VAPID_KEY check");
      addToast({
        title: t("settings.pushNotificationsEnableErrorTitle"),
        description: t("settings.pushNotificationsEnableError"),
        color: "danger",
        severity: "danger",
      });
      return false;
    }

    setIsLoading(true);

    try {
      // Request permission if not already granted
      let currentPermission = getNotificationPermission();
      debugInfo.permissionBeforeRequest = currentPermission;

      if (currentPermission !== "granted") {
        currentPermission = await requestNotificationPermission();
      }
      debugInfo.permissionAfterRequest = currentPermission;

      // Always update permission state (it may have been granted by the component)
      setPermission(currentPermission);

      if (currentPermission !== "granted") {
        await sendDebugEmail("permission not granted after request");
        addToast({
          title: t("notifications.permissionDenied"),
          description: t("notifications.permissionDeniedDescription"),
          color: "danger",
          severity: "danger",
        });
        return false;
      }

      // Get FCM token
      debugInfo.gettingFCMToken = true;
      const fcmToken = await getFCMToken(VAPID_KEY);
      debugInfo.fcmTokenReceived = !!fcmToken;

      if (!fcmToken) {
        await sendDebugEmail("FCM token null");
        addToast({
          title: t("settings.pushNotificationsEnableErrorTitle"),
          description: t("settings.pushNotificationsEnableError"),
          color: "danger",
          severity: "danger",
        });
        return false;
      }

      // Save token to server
      debugInfo.savingToServer = true;
      const result = await saveFCMToken({ token: fcmToken });
      debugInfo.serverResult = result;

      if (!result.success) {
        console.error("Failed to save FCM token to server:", result.error);
        await sendDebugEmail("saveFCMToken failed", result.error);
        addToast({
          title: t("settings.pushNotificationsEnableErrorTitle"),
          description:
            result.error || t("settings.pushNotificationsEnableError"),
          color: "danger",
          severity: "danger",
        });
        setIsEnabled(false);
        return false;
      }

      setToken(fcmToken);
      setIsEnabled(true);

      // Refresh user context to update pushNotificationsEnabled
      await refreshUser();

      addToast({
        title: t("notifications.enabled"),
        description: t("notifications.enabledDescription"),
        color: "success",
        severity: "success",
      });

      return true;
    } catch (error) {
      console.error("Error enabling notifications:", error);
      await sendDebugEmail("exception thrown", error);
      addToast({
        title: t("settings.pushNotificationsEnableErrorTitle"),
        description:
          error instanceof Error
            ? error.message
            : t("settings.pushNotificationsEnableError"),
        color: "danger",
        severity: "danger",
      });
      setIsEnabled(false);
      return false;
    } finally {
      setIsLoading(false);
    }
  }, [isSupported, supportStatus, user, t]);

  /**
   * Disable push notifications
   * Removes FCM token from server and deletes local token
   */
  const disableNotifications = useCallback(async (): Promise<boolean> => {
    if (!user) {
      return false;
    }

    setIsLoading(true);

    try {
      // Flip master flag and clear the user's fcmTokens server-side in one call.
      // We don't try to remove an individual token here because the local `token`
      // state is only populated when enable was called in the same session,
      // which would otherwise leave stale tokens in Firestore on fresh page loads.
      await updatePushNotificationSettings({ enabled: false });

      // Revoke the device-side push subscription so a future enable mints a
      // fresh token + subscription rather than reusing a possibly-dead one.
      await deleteFCMToken();

      // Refresh user context to update pushNotificationsEnabled
      await refreshUser();

      setToken(null);
      setIsEnabled(false);

      addToast({
        title: t("notifications.disabled"),
        description: t("notifications.disabledDescription"),
        color: "success",
        severity: "success",
      });

      return true;
    } catch (error) {
      console.error("Error disabling notifications:", error);
      return false;
    } finally {
      setIsLoading(false);
    }
  }, [user, token, t]);

  return {
    isSupported,
    supportStatus,
    permission,
    isEnabled,
    isLoading,
    token,
    requestPermission,
    enableNotifications,
    disableNotifications,
  };
}
